// @ts-strict-ignore
import { cookieExtractor } from "@shared/helpers/cookieExtractor/cookieExtractor";
import type { RouteObject } from "@shared/react-router-dom/react-router-dom";
import type { Flipper } from "@shared/types/flipper";
import { wildcardPath } from "@shared/utils/wildcardPath/wildcardPath";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import { ContinueWithoutPasswordPage } from "@unauthenticated/signupSignin/scenes/ContinueWithoutPasswordPage/ContinueWithoutPasswordPage";
import { LazyEditPasswordPage } from "@unauthenticated/signupSignin/scenes/EditPasswordPage/LazyEditPasswordPage";
import { LazyForgotPasswordPage } from "@unauthenticated/signupSignin/scenes/ForgotPasswordPage/LazyForgotPasswordPage";
import { PatientSignUpPage } from "@unauthenticated/signupSignin/scenes/PatientSignUpPage/PatientSignUpPage";
import { PractitionerSignUpPage } from "@unauthenticated/signupSignin/scenes/PractitionerSignUpPage/PractitionerSignUpPage";
import { SetupMFAPage } from "@unauthenticated/signupSignin/scenes/SetupMFAPage/SetupMFAPage";
import { LazySignInPage } from "@unauthenticated/signupSignin/scenes/SignInPage/LazySignInPage";
import { VerifyEmailPage } from "@unauthenticated/signupSignin/scenes/VerifyEmailPage/VerifyEmailPage";

import { Root, type Props as RootProps } from "./Root";

type Route = RouteObject & {
  flipper?: Flipper;
};

interface Props extends RootProps {
  signUpAdditionalAttributes?: any;
  signInAdditionalAttributes?: any;
  ssoErrors?: string;
  authErrors?: string;
}

const fullscriptRoutes = ({
  authErrors,
  pageVersion,
  signInAdditionalAttributes,
  signUpAdditionalAttributes,
  ssoErrors,
  mfaToken,
}: Props): Route[] => [
  {
    path: wildcardPath(authRoutes.edit_password),
    element: <LazyEditPasswordPage />,
  },
  {
    path: wildcardPath(authRoutes.forgot_password),
    element: <LazyForgotPasswordPage />,
  },
  {
    path: wildcardPath(authRoutes.login),
    element: (
      <LazySignInPage
        ssoErrors={ssoErrors}
        authErrors={authErrors}
        additionalAttributes={signInAdditionalAttributes}
      />
    ),
  },
  {
    path: wildcardPath(authRoutes.activateKit),
    element: (
      <LazySignInPage
        ssoErrors={ssoErrors}
        authErrors={authErrors}
        additionalAttributes={{ ...signInAdditionalAttributes, redirectPath: "/activate-kit" }}
      />
    ),
  },
  {
    path: wildcardPath(authRoutes.continue_without_password),
    element: <ContinueWithoutPasswordPage />,
  },
  {
    path: wildcardPath(authRoutes.verify_email),
    element: <VerifyEmailPage />,
  },
  {
    path: wildcardPath(authRoutes.setup_mfa),
    element: <SetupMFAPage mfaToken={mfaToken} />,
    flipper: "iam_mandatory_prac_mfa",
  },
  {
    path: wildcardPath(authRoutes.patient_sign_up),
    element: <PatientSignUpPage {...{ pageVersion }} />,
  },
  {
    path: wildcardPath(authRoutes.practitioner_sign_up),
    element: (
      <PractitionerSignUpPage
        {...{
          additionalAttributes: signUpAdditionalAttributes,
          ssoErrors,
        }}
      />
    ),
  },
];

const routeActive = (route: Route, flippers: Flipper[]) => {
  return !route.flipper || flippers.includes(route.flipper);
};

const activeRoutes = (allRoutes: Route[], flippers: Flipper[]) => {
  return allRoutes
    .filter(route => routeActive(route, flippers))
    .map(({ flipper: unusedFlipper, ...route }) => route);
};

const getFullscriptRoutes = (props: Props): Route[] => {
  const { _gid: googleId, _ga: googleAnalyticsId } = cookieExtractor(["_gid", "_ga"]);

  const optionalAttributesForSignUp = {
    ...props.signUpAdditionalAttributes,
    googleId,
    googleAnalyticsId,
  };

  const allRoutes = fullscriptRoutes({
    ...props,
    signUpAdditionalAttributes: optionalAttributesForSignUp,
  });

  return [
    {
      path: "/",
      element: <Root {...{ ...props, signUpAdditionalAttributes: optionalAttributesForSignUp }} />,
      children: activeRoutes(allRoutes, props.flippers),
    },
  ];
};

export type { Props };
export { getFullscriptRoutes };
